import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withKeys as _withKeys, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "header__title" }
const _hoisted_4 = { class: "content" }
const _hoisted_5 = { class: "content__description" }
const _hoisted_6 = { class: "content__create-act" }
const _hoisted_7 = {
  key: 0,
  class: "content__create-act-selections"
}
const _hoisted_8 = {
  key: 1,
  class: "content__disabled-drop-zone"
}
const _hoisted_9 = {
  key: 0,
  class: "content__disabled-drop-zone__error-text"
}
const _hoisted_10 = { for: "file-input" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "content__drop-zone__add-file__text" }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "content__drop-zone__add-file__icon" }
const _hoisted_15 = { class: "content__drop-zone__add-file__text" }
const _hoisted_16 = { class: "content__drop-zone__add-file__text__highlight" }
const _hoisted_17 = {
  key: 3,
  class: "content__file-list"
}
const _hoisted_18 = ["file"]
const _hoisted_19 = { class: "content__file-list__row__text" }
const _hoisted_20 = { class: "content__file-list__row__status" }
const _hoisted_21 = {
  key: 0,
  class: "content__file-list__row__status__loading-spinner"
}
const _hoisted_22 = ["onClick"]
const _hoisted_23 = {
  key: 0,
  class: "footer"
}

import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { useSelectionStore } from '@/pinia/selection'

import { ActCategory, getActCategories } from '@/services/acts.service'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'

import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'
import IconPaperclip from '@/design-system/assets/icons/IconPaperclip.vue'
import IconUpload from '@/design-system/assets/icons/IconUpload.vue'
import IconX from '@/design-system/assets/icons/IconX.vue'
import IconXCircleMedium from '@/design-system/assets/icons/IconXCircleMedium.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import InputFileDrop from '@/design-system/atoms/InputFileDrop.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import useFileList from '@/hooks/attachments/useFileList'
import createActUploader from '@/hooks/attachments/useActUploader'
import createFileUploader from '@/hooks/attachments/useFileUploader'

// emits
interface EMITS {
  (e: 'closeUploadFilesModal', val: boolean)
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ModalUploadFiles',
  emits: ["closeUploadFilesModal"],
  setup(__props, { emit: __emit }) {

const emits = __emit

// variables
const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()
const { filesUpload, addFiles, removeFile } = useFileList()
const { uploadActs } = createActUploader()
const { uploadFiles } = createFileUploader()
const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)

const actCategories = ref<ActCategory[]>([])
const actCategoryOptions = ref<optionType[]>([])
const actStatusOptions = ref<optionType[]>([])
const actTypeOptions = ref<optionType[]>([])
const createActChecked = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const isLoadingActCategories = ref(false)

// computed
const selectedObject = computed(() => {
  return store.getters.selectedObject
})

const descriptionText = computed<string>(() => {
  let description = ''
  if (!selectedObject.value && selectedObject.value !== 0) return description
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return description
  if (selectedObject.value === 0) {
    // 0 is for overview (REGION)
    description = `${t('message__files-upload__part1')} ${t(
      'message__files-upload__part2-no-connection'
    )}`
  } else {
    // Additional text for LEVEL
    const descriptionOutro =
      selectedObject.value.type === 'LEVEL'
        ? `${t('message__files-upload__part2-connection')} ${t(
            'general__text__level'
          ).toLowerCase()}`
        : `${t('message__files-upload__part2-connection')}`
    // For level, level is used instead of name
    // For complex, title is used instead of name
    description = `${t('message__files-upload__part1')} ${descriptionOutro} ${
      selectedObject.value.name
        ? selectedObject.value.type === 'LEVEL'
          ? `"${selectedObject.value.level}"`
          : `"${selectedObject.value.name}"`
        : `"${selectedObject.value.title}"`
    }.`
  }
  return description
})
const isUploadingFiles = computed(() => {
  return filesUpload.value.some((file) => file.status === 'loading' || file.status === null)
})
const selectedActCategory = computed(() => {
  return actCategoryOptions.value.find((category) => category.selected)
})
const selectedActStatus = computed(() => {
  return actStatusOptions.value.find((status) => status.selected)
})
const selectedActType = computed(() => {
  return actTypeOptions.value.find((type) => type.selected)
})
const showInputFileDrop = computed(() => {
  return (
    !createActChecked.value ||
    (createActChecked.value &&
      !isLoadingActCategories.value &&
      selectedActCategory.value &&
      selectedActStatus.value &&
      selectedActType.value)
  )
})
const validationMessage = computed<string>(() => {
  return `${t('general__text__select')} ${t('general__text__category').toLowerCase()}, ${t(
    'general__text__status'
  ).toLowerCase()} ${t('general__text__and').toLowerCase()} ${t(
    'general__text__type'
  ).toLowerCase()}`
})

// methods
function clickFileInput(event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  if (fileInput.value) fileInput.value.click()
}
function closeModal() {
  piniaStore.setFilesUploaded(null)
  emits('closeUploadFilesModal', false)
}
function handleActCategoryDropdownSelection(event: string) {
  actStatusOptions.value = []
  actTypeOptions.value = []
  const selectedCategory = actCategories.value.find((category) => category.id === event)
  if (selectedCategory) setOptionsForActCategory(selectedCategory)
}
function onInputChange(event) {
  addFiles(event.target.files)
  event.target.value = null
}
function resetActCategories() {
  actCategories.value = []
  actCategoryOptions.value = []
  actStatusOptions.value = []
  actTypeOptions.value = []
}
function setOptionsForActCategory(category: ActCategory) {
  actStatusOptions.value = category.statuses.map((status) => {
    return {
      id: status.id,
      name: status.name,
      selected: false,
      disabled: false
    }
  })
  actTypeOptions.value = category.types.map((type) => {
    return {
      id: type.id,
      name: type.name,
      selected: false,
      disabled: false
    }
  })
}

// watches
watch(createActChecked, async (isChecked) => {
  if (isChecked) {
    isLoadingActCategories.value = true
    const actCategoriesResponse = await getActCategories()
    actCategories.value = actCategoriesResponse ?? []
    if (actCategories.value.length) {
      actCategoryOptions.value = actCategories.value.map((category) => {
        return {
          id: category.id,
          name: category.name,
          selected: false,
          disabled: false
        }
      })
      setOptionsForActCategory(actCategories.value[0])
    }
    isLoadingActCategories.value = false
  } else {
    resetActCategories()
  }
})
watch(filesUpload, async () => {
  if (filesUpload.value.length) {
    const selectedEntityType =
      clickedEntityType.value === 'REGION' ? 'REALM' : clickedEntityType.value
    if (!createActChecked.value) {
      await uploadFiles(filesUpload.value, store.getters.selectedObjectId, selectedEntityType)
    } else {
      if (selectedActCategory.value && selectedActStatus.value && selectedActType.value) {
        await uploadActs(
          filesUpload.value,
          store.getters.selectedObjectId,
          selectedEntityType,
          selectedActCategory.value.id,
          selectedActType.value.id,
          selectedActStatus.value.id
        )
      }
    }
    // Add files to pinia store for refreshing list in FilesDashboard
    let uploadedFilesArray: string[] = []
    filesUpload.value.forEach((file) => {
      if (file.status === true) uploadedFilesArray.push(file.id)
    })
    piniaStore.setFilesUploaded(uploadedFilesArray)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LayoutOverlay, {
    class: "modal-upload-files",
    onCloseOverlay: _cache[5] || (_cache[5] = ($event: any) => (closeModal()))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(t)('general__data__file-upload-v2')), 1),
          (!_unref(filesUpload).length)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "header__close-button",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (closeModal()))
              }, [
                _createVNode(IconXCircleMedium, { class: "header__close-button__icon" })
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("p", _hoisted_5, _toDisplayString(descriptionText.value), 1),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(CheckBoxStandard, {
              class: "content__create-act__checkbox",
              checked: createActChecked.value,
              disabled: _unref(filesUpload).length > 0,
              title: _unref(t)('general__data__file-upload-as-act'),
              onValueChanged: _cache[1] || (_cache[1] = ($event: any) => (createActChecked.value = !createActChecked.value))
            }, null, 8, ["checked", "disabled", "title"]),
            _createVNode(InfoLabel, {
              class: "content__create-act__info",
              info: _unref(t)('tooltip__files-create-act')
            }, null, 8, ["info"])
          ]),
          (createActChecked.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(DropdownStandard, {
                  class: "content__create-act-selections__dropdown",
                  disabled: !actCategoryOptions.value.length || _unref(filesUpload).length > 0,
                  label: _unref(t)('general__text__category'),
                  title: 
              selectedActCategory.value?.name ??
              _unref(t)('general__text__select') + ' ' + _unref(t)('general__text__category').toLowerCase()
            ,
                  options: actCategoryOptions.value,
                  required: true,
                  onSelect: _cache[2] || (_cache[2] = ($event: any) => (handleActCategoryDropdownSelection($event)))
                }, null, 8, ["disabled", "label", "title", "options"]),
                _createVNode(DropdownStandard, {
                  class: "content__create-act-selections__dropdown",
                  disabled: !actTypeOptions.value.length || _unref(filesUpload).length > 0,
                  label: _unref(t)('general__text__type'),
                  title: 
              selectedActType.value?.name ??
              _unref(t)('general__text__select') + ' ' + _unref(t)('general__text__type').toLowerCase()
            ,
                  options: actTypeOptions.value,
                  required: true
                }, null, 8, ["disabled", "label", "title", "options"]),
                _createVNode(DropdownStandard, {
                  class: "content__create-act-selections__dropdown",
                  disabled: !actStatusOptions.value.length || _unref(filesUpload).length > 0,
                  label: _unref(t)('general__text__status'),
                  title: 
              selectedActStatus.value?.name ??
              _unref(t)('general__text__select') + ' ' + _unref(t)('general__text__status').toLowerCase()
            ,
                  options: actStatusOptions.value,
                  required: true
                }, null, 8, ["disabled", "label", "title", "options"])
              ]))
            : _createCommentVNode("", true),
          (
            isLoadingActCategories.value ||
            (createActChecked.value && !isLoadingActCategories.value && !showInputFileDrop.value)
          )
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createVNode(PulseLoader, {
                  loading: isLoadingActCategories.value,
                  class: "spinner",
                  color: "#1d234f"
                }, null, 8, ["loading"]),
                (!isLoadingActCategories.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(validationMessage.value), 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (showInputFileDrop.value)
            ? (_openBlock(), _createBlock(InputFileDrop, {
                key: 2,
                class: "content__drop-zone",
                tabindex: "0",
                onFilesDropped: _unref(addFiles),
                onKeydown: [
                  _withKeys(clickFileInput, ["enter"]),
                  _cache[3] || (_cache[3] = _withKeys((event) => clickFileInput(event, true), ["space"]))
                ]
              }, {
                default: _withCtx(({ dropZoneActive }) => [
                  _createElementVNode("label", _hoisted_10, [
                    dropZoneActive
                      ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                          _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(t)('general__data__file-drop')), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_13, [
                          _createElementVNode("span", _hoisted_14, [
                            _createVNode(IconUpload)
                          ]),
                          _createElementVNode("span", _hoisted_15, [
                            _createElementVNode("span", _hoisted_16, _toDisplayString(_unref(t)('general__data__file-add')), 1),
                            _createTextVNode(" " + _toDisplayString(_unref(t)('general__data__file-drop-or').toLowerCase()) + " ", 1),
                            _cache[6] || (_cache[6] = _createElementVNode("br", null, null, -1)),
                            _createTextVNode(" " + _toDisplayString(_unref(t)('general__data__upload-will-start').toLowerCase()), 1)
                          ])
                        ])),
                    _createElementVNode("input", {
                      id: "file-input",
                      ref_key: "fileInput",
                      ref: fileInput,
                      type: "file",
                      multiple: "",
                      onChange: onInputChange
                    }, null, 544)
                  ])
                ]),
                _: 1
              }, 8, ["onFilesDropped"]))
            : _createCommentVNode("", true),
          (_unref(filesUpload).length)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_17, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filesUpload), (file) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: file.id,
                    class: _normalizeClass(["content__file-list__row", { 'content__file-list__row__error': file.status === false }]),
                    file: file
                  }, [
                    _createVNode(IconPaperclip, { class: "content__file-list__row__icon" }),
                    _createElementVNode("span", _hoisted_19, _toDisplayString(file.file.name), 1),
                    _createElementVNode("span", _hoisted_20, [
                      (file.status === 'loading')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                            _createVNode(PulseLoader, {
                              color: "#3f46d6",
                              size: "8px"
                            })
                          ]))
                        : _createCommentVNode("", true),
                      (file.status === true)
                        ? (_openBlock(), _createBlock(IconCheck, {
                            key: 1,
                            class: "content__file-list__row__status__icon-success"
                          }))
                        : _createCommentVNode("", true),
                      (file.status === false)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 2,
                            class: "content__file-list__row__status__button",
                            "aria-label": "Remove failed file",
                            onClick: ($event: any) => (_unref(removeFile)(file))
                          }, [
                            _createVNode(IconX, { class: "content__file-list__row__status__button__icon-error" })
                          ], 8, _hoisted_22))
                        : _createCommentVNode("", true)
                    ])
                  ], 10, _hoisted_18))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        (_unref(filesUpload).length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createVNode(ButtonPrimary, {
                class: "footer__button",
                buttonText: _unref(t)('general__text__done'),
                disabled: isUploadingFiles.value,
                onOnClicked: _cache[4] || (_cache[4] = ($event: any) => (closeModal()))
              }, null, 8, ["buttonText", "disabled"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})